import React, { useEffect, useState, useRef, useCallback } from "react";
import "./Countdown.css";

type CountdownProps = {
  targetDate: string; // ISO date string, e.g. "2026-07-20T15:00:00"
};

function pad(num: number) {
  return String(num).padStart(2, "0");
}

export default function Countdown({ targetDate }: CountdownProps) {
  const target = new Date(targetDate);
  const [now, setNow] = useState<Date>(new Date());
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const particlesRef = useRef<any[]>([]);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const diff = Math.max(0, target.getTime() - now.getTime());

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const seconds = Math.floor((diff / 1000) % 60);

  const finished = diff <= 0;

  const launchBurst = useCallback((w: number, h: number) => {
    const cx = Math.random() * w;
    const cy = Math.random() * (h * 0.6);
    const hue = Math.floor(Math.random() * 360);
    const count = 30 + Math.floor(Math.random() * 30);
    for (let i = 0; i < count; i++) {
      const speed = 1 + Math.random() * 4;
      const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.5;
      particlesRef.current.push({
        x: cx,
        y: cy,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 60 + Math.random() * 40,
        age: 0,
        size: 2 + Math.random() * 3,
        hue,
      });
    }
  }, []);

  useEffect(() => {
    if (!finished) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let last = performance.now();

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.floor(canvas.clientWidth * dpr);
      canvas.height = Math.floor(canvas.clientHeight * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    resize();
    window.addEventListener("resize", resize);

    const tick = (t: number) => {
      const dt = Math.min(50, t - last);
      last = t;

      // occasionally launch a new burst
      if (Math.random() < 0.05) launchBurst(canvas.clientWidth, canvas.clientHeight);

      ctx.clearRect(0, 0, canvas.clientWidth, canvas.clientHeight);

      const next: any[] = [];
      for (const p of particlesRef.current) {
        p.vy += 0.03 * (dt / 16);
        p.vx *= 0.998;
        p.x += p.vx * (dt / 16);
        p.y += p.vy * (dt / 16);
        p.age += dt / 16;

        const alpha = Math.max(0, 1 - p.age / p.life);
        ctx.beginPath();
        ctx.fillStyle = `hsla(${p.hue},80%,60%,${alpha})`;
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();

        if (p.age < p.life) next.push(p);
      }
      particlesRef.current = next;

      rafRef.current = requestAnimationFrame(tick);
    };

    // start with a few bursts
    for (let i = 0; i < 3; i++) launchBurst(canvas.clientWidth, canvas.clientHeight);
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
      particlesRef.current = [];
    };
  }, [finished, launchBurst]);

  return (
    <div className="countdown-wrapper" aria-live="polite">
      {finished ? (
        <>
          <div className="countdown-finished">
            JUST MARRIED!!
          </div>
          <canvas
            ref={canvasRef}
            className="countdown-canvas"
          />
        </>
      ) : (
        <div className="countdown-container">
          <div className="countdown-box">
            <div className="countdown-value">{days}</div>
            <div className="countdown-label">Dager</div>
          </div>
          <div className="countdown-box">
            <div className="countdown-value">{pad(hours)}</div>
            <div className="countdown-label">Timer</div>
          </div>
          <div className="countdown-box">
            <div className="countdown-value">{pad(minutes)}</div>
            <div className="countdown-label">Minutter</div>
          </div>
          <div className="countdown-box">
            <div className="countdown-value">{pad(seconds)}</div>
            <div className="countdown-label">Sekunder</div>
          </div>
        </div>
      )}
    </div>
  );
}
